// Bulk Upload Dialog Component for AI-powered document classification
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select';
import { Upload, FileText, Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const BulkUploadDialog = ({ open, onClose, saleOpportunityId, horseDeliverables, ownerDeliverables, onUploadComplete }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Review, 3: Processing
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [allHorseTypes, setAllHorseTypes] = useState([]);
  const [allOwnerTypes, setAllOwnerTypes] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleClassify = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setProcessing(true);
    setProgress(10);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('files', file));
      formData.append('sale_opportunity_id', saleOpportunityId);

      setProgress(30);

      const response = await api.post('/deliverables/bulk-upload/classify', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProgress(80);

      const results = response.data;
      setClassifications(results.classifications);
      setAllHorseTypes(results.all_horse_types || []);
      setAllOwnerTypes(results.all_owner_types || []);
      
      setProgress(100);
      setStep(2);
    } catch (error) {
      console.error('Classification error:', error);
      toast.error('Failed to classify documents');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmUpload = async () => {
    setProcessing(true);
    setProgress(0);
    setStep(3);

    try {
      // First, create any new deliverables that were requested
      const newDeliverables = {};
      
      for (const c of classifications) {
        const createNew = c.create_new_deliverable_for || [];
        for (let idx = 0; idx < createNew.length; idx++) {
          const deliverableType = createNew[idx];
          if (deliverableType) {
            try {
              // Create new horse deliverable
              const newDel = await api.post('/sale-deliverables', {
                sale_opportunity_id: saleOpportunityId,
                deliverable_type_horse: deliverableType,
                status: 'not_started'
              });
              
              // Store the new deliverable ID for this classification
              newDeliverables[`${c.filename}-${idx}`] = newDel.data.id;
              
              toast.success(`Created new ${deliverableType.replace(/_/g, ' ')} deliverable`);
            } catch (error) {
              console.error(`Error creating deliverable ${deliverableType}:`, error);
              toast.error(`Failed to create ${deliverableType} deliverable`);
            }
          }
        }
      }
      
      // Flatten: one file might go to multiple deliverables, skip rejected detections
      const uploads = [];
      
      classifications.forEach(c => {
        const deliverableIds = c.selected_deliverable_ids || [];
        const skippedIndices = c.skipped_indices || [];
        const deliverables = c.deliverables_found || [];
        
        deliverableIds.forEach((deliverableId, idx) => {
          // Only include if not skipped and has a deliverable assigned
          if (!skippedIndices.includes(idx)) {
            // Check if this was a "create_new" that we just created
            let actualDeliverableId = deliverableId;
            if (deliverableId === 'create_new') {
              actualDeliverableId = newDeliverables[`${c.filename}-${idx}`];
            }
            
            if (actualDeliverableId && actualDeliverableId !== 'create_new') {
              const deliverable = deliverables[idx] || {};
              uploads.push({
                filename: c.filename,
                temp_path: c.temp_path,
                deliverable_id: actualDeliverableId,
                deliverable_type: deliverable.deliverable_type || 'unknown',
                is_owner_deliverable: deliverable.is_owner_deliverable || false,
                pages: deliverable.pages || 'all'  // Include page specification for extraction
              });
            }
          }
        });
      });

      if (uploads.length === 0) {
        toast.error('No documents to upload - all detections were skipped');
        setStep(2);
        setProcessing(false);
        return;
      }

      const response = await api.post('/deliverables/bulk-upload/confirm', { uploads });

      const { success, failed } = response.data;
      
      if (success.length > 0) {
        toast.success(`Successfully uploaded ${success.length} document(s)`);
      }
      if (failed.length > 0) {
        toast.error(`Failed to upload ${failed.length} document(s)`);
      }

      if (onUploadComplete) {
        await onUploadComplete();
        // Give the parent component time to update state before closing
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload documents');
      setStep(2);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedFiles([]);
    setClassifications([]);
    setProgress(0);
    onClose();
  };

  const updateClassification = (index, deliverableId) => {
    const updated = [...classifications];
    updated[index].selected_deliverable_id = deliverableId;
    setClassifications(updated);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Upload Documents</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-4">
                Upload multiple PDFs - AI will automatically classify them
              </p>
              <input
                type="file"
                multiple
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                id="bulk-upload-input"
              />
              <label htmlFor="bulk-upload-input">
                <Button variant="outline" asChild>
                  <span style={{cursor: 'pointer'}}>Select PDF Files</span>
                </Button>
              </label>
            </div>

            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{selectedFiles.length} file(s) selected:</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span>{file.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {processing && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-sm text-center text-muted-foreground">
                  Analyzing documents with AI...
                </p>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-900">
                <strong>Review AI classifications</strong> - Confirm or adjust the deliverable assignment for each document
              </p>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {classifications.map((c, idx) => {
                const deliverables = c.deliverables_found || [];
                const hasError = c.error || deliverables.length === 0;
                const hasMultiple = c.multiple_matches;

                if (hasError) {
                  return (
                    <div key={idx} className="border border-red-200 bg-red-50 rounded p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                        <span className="font-medium text-sm">{c.filename}</span>
                        <Badge variant="destructive">Error</Badge>
                      </div>
                      <p className="text-xs text-red-700">
                        {c.error || 'No deliverables detected - please select manually'}
                      </p>
                      <div className="mt-3">
                        <Label className="text-xs">Assign to Deliverable:</Label>
                        <Select
                          value={c.selected_deliverable_ids?.[0] || ''}
                          onValueChange={(value) => {
                            const updated = [...classifications];
                            updated[idx].selected_deliverable_ids = [value];
                            setClassifications(updated);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select deliverable..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectLabel>Horse Deliverables</SelectLabel>
                              {horseDeliverables.map(d => (
                                <SelectItem key={d.id} value={d.id}>
                                  {d.deliverable_type_horse?.replace(/_/g, ' ')}
                                  {d.nickname && ` (${d.nickname})`}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                            <SelectGroup>
                              <SelectLabel>Owner Deliverables</SelectLabel>
                              {ownerDeliverables.map(d => (
                                <SelectItem key={d.id} value={d.id}>
                                  {d.deliverable_type?.replace(/_/g, ' ')}
                                  {d.property_line && ` - ${d.property_line}`}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={idx} className="border border-border rounded p-3">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span className="font-medium text-sm">{c.filename}</span>
                        {hasMultiple && (
                          <Badge variant="outline" className="bg-yellow-50">
                            {deliverables.length} deliverables detected
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {deliverables.map((classification, delIdx) => {
                        const isSkipped = c.skipped_indices?.includes(delIdx);
                        
                        return (
                          <div key={delIdx} className={`pl-4 border-l-2 ${isSkipped ? 'border-red-200 bg-red-50/50' : 'border-muted'} space-y-2 p-2 rounded`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-xs font-medium">
                                    {classification.deliverable_type?.replace(/_/g, ' ').toUpperCase()}
                                    {classification.pages && classification.pages !== 'unknown' && (
                                      <span className="ml-2 text-muted-foreground font-normal">
                                        (Pages: {classification.pages})
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <p className="text-xs text-muted-foreground">{classification.reasoning || 'No reasoning provided'}</p>
                                {isSkipped && (
                                  <p className="text-xs text-red-600 mt-1">✗ Skipped - will not be uploaded</p>
                                )}
                              </div>
                              <Badge variant={classification.confidence === 'high' ? 'default' : 'outline'} className="text-xs">
                                {classification.confidence || 'unknown'}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <Label className="text-xs">
                                  Assign to Deliverable:
                                  {classification.is_preselected === false && (
                                    <span className="ml-2 text-amber-600 font-medium">(Not pre-selected - can create)</span>
                                  )}
                                </Label>
                                <Select
                                  value={c.selected_deliverable_ids?.[delIdx] || ''}
                                  onValueChange={(value) => {
                                    const updated = [...classifications];
                                    if (!updated[idx].selected_deliverable_ids) {
                                      updated[idx].selected_deliverable_ids = [];
                                    }
                                    
                                    // Handle "create_new" option
                                    if (value === 'create_new') {
                                      updated[idx].create_new_deliverable_for = updated[idx].create_new_deliverable_for || [];
                                      updated[idx].create_new_deliverable_for[delIdx] = classification.deliverable_type;
                                      updated[idx].selected_deliverable_ids[delIdx] = 'create_new';
                                    } else {
                                      updated[idx].selected_deliverable_ids[delIdx] = value;
                                    }
                                    
                                    // If user selects a deliverable, remove from skipped
                                    if (value && updated[idx].skipped_indices?.includes(delIdx)) {
                                      updated[idx].skipped_indices = updated[idx].skipped_indices.filter(i => i !== delIdx);
                                    }
                                    
                                    setClassifications(updated);
                                  }}
                                  disabled={isSkipped}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={isSkipped ? "Skipped" : "Select deliverable..."} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {classification.is_preselected === false && (
                                      <SelectGroup>
                                        <SelectLabel>Create New</SelectLabel>
                                        <SelectItem value="create_new">
                                          + Create new {classification.deliverable_type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} deliverable
                                        </SelectItem>
                                      </SelectGroup>
                                    )}
                                    <SelectGroup>
                                      <SelectLabel>Horse Deliverables</SelectLabel>
                                      {horseDeliverables.map(d => (
                                        <SelectItem key={d.id} value={d.id}>
                                          {d.deliverable_type_horse?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                          {d.nickname && ` (${d.nickname})`}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                    <SelectGroup>
                                      <SelectLabel>Owner Deliverables</SelectLabel>
                                      {(() => {
                                        // Deduplicate owner deliverables by type
                                        const seen = new Set();
                                        return ownerDeliverables.filter(d => {
                                          const key = d.deliverable_type + (d.property_line || '');
                                          if (seen.has(key)) return false;
                                          seen.add(key);
                                          return true;
                                        }).map(d => (
                                          <SelectItem key={d.id} value={d.id}>
                                            {d.deliverable_type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                            {d.property_line && ` - ${d.property_line}`}
                                          </SelectItem>
                                        ));
                                      })()}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <Button
                                variant={isSkipped ? "outline" : "ghost"}
                                size="sm"
                                onClick={() => {
                                  const updated = [...classifications];
                                  if (!updated[idx].skipped_indices) {
                                    updated[idx].skipped_indices = [];
                                  }
                                  
                                  if (isSkipped) {
                                    // Un-skip
                                    updated[idx].skipped_indices = updated[idx].skipped_indices.filter(i => i !== delIdx);
                                  } else {
                                    // Skip
                                    updated[idx].skipped_indices.push(delIdx);
                                    // Clear selection
                                    if (updated[idx].selected_deliverable_ids) {
                                      updated[idx].selected_deliverable_ids[delIdx] = null;
                                    }
                                  }
                                  
                                  setClassifications(updated);
                                }}
                                className={isSkipped ? "border-red-600 text-red-600" : "text-muted-foreground hover:text-red-600"}
                                title={isSkipped ? "Include this detection" : "Skip/reject this detection"}
                              >
                                {isSkipped ? "Include" : "Skip"}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {hasMultiple && deliverables.some((d, i) => !(c.skipped_indices?.includes(i))) && (
                      <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded">
                        <p className="text-xs text-amber-900">
                          ℹ️ This PDF will be uploaded to {deliverables.filter((d, i) => !(c.skipped_indices?.includes(i))).length} deliverable(s). 
                          Skipped detections will not be saved.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="py-12 text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-3 text-primary" />
            <p className="text-sm text-muted-foreground">Uploading documents...</p>
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleClassify} 
                disabled={selectedFiles.length === 0 || processing}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                {processing ? 'Analyzing...' : 'Classify Documents'}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button 
                onClick={handleConfirmUpload}
                disabled={classifications.some(c => {
                  const ids = c.selected_deliverable_ids || [];
                  const found = c.deliverables_found || [];
                  return found.length > 0 && ids.filter(Boolean).length === 0;
                })}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                Confirm & Upload
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadDialog;
